import { Key, useEffect, useState } from 'react';
import { AxiosInstance } from 'axios';
import { t } from 'i18next';
import {
  Accordion,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemState
} from 'react-accessible-accordion';
import { AiOutlineMinusCircle, AiOutlinePlusCircle } from 'react-icons/ai';
import { RxCross2 } from 'react-icons/rx';
import * as Dialog from '@radix-ui/react-dialog';

import './styles.css';

import { DataRequest } from '../../store/services/requestScreen/TypesRequestScreen';

import { AccountService } from '../../services/AccountService';
import { useApi } from '../../services/hooks/apiHook';

import { errorToast } from '../../utils/toasts';
import {
  StyledAccordionItem,
  StyledButton,
  StyledHeading,
  StyledModalButton,
  StyledModalButtonHandle,
  StyledProcessingTime,
  StyledProcessingTimeContainer,
  StyledSteps,
  StyledStepsContainer,
  StyledSubTitle,
  StyledValue,
  StyledValueContainer
} from './styledGuide';
import { RootState, useAppDispatch } from '../../store';
import { useAppSelector } from '../../hooks/hooksRedux';
import { defaultBorder, defaultColors } from '../../styles/style-guide';

export const CollapsibleCard = ({ dataResponse, headers, companyID }: any) => {
  const [showModal, setModal] = useState<boolean>(false);
  const [isPaymentUrl, setIsPaymentUrl] = useState<boolean>(false);
  const [paymentUrl, setPaymentUrl] = useState<string>('');
  const [addRequestId, setAddRequestId] = useState<string>('');
  const api: AxiosInstance = useApi();

  const { colors, border } = useAppSelector((state: RootState) => state.styleGuide.data);

  const primaryColor = colors.primary || defaultColors.primary;
  const primaryBorderColor = border.primary || defaultBorder.primary;


  const { data } = dataResponse[0];

  const formatterValue = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  });

  const handleShowModal = (active: boolean, requestId: string) => () => {
    if (active) {
      setAddRequestId(requestId);
      setModal(true);
      return true;
    }
    return false;
  };

  const handleAddRequest = async () => {
    const payloadRequest = {
      company_id: companyID.id,
      service_request_id: addRequestId
    };

    try {
      if (addRequestId && companyID.id) {
        const { payment_url } = await AccountService.addRequests(api, payloadRequest, { headers: headers });
        if (payment_url) {
          setIsPaymentUrl(true);
          setPaymentUrl(payment_url);
          window.open(payment_url, '_blank');
        }
      }
    } catch (error) {
      console.error('Add Request error ', error);

      errorToast(t('errorMessage.errMessAddRequest'));
    }
  };

  useEffect(() => {
    if (isPaymentUrl) {
      setModal(false);
    }
  }, [paymentUrl]);

  return (
    <>
      <Accordion allowZeroExpanded>
        {data.map((request: DataRequest, index: Key) => (
          <div className="mt-4" key={index}>
            <StyledAccordionItem uuid={request.id} key={request.id}>
              <AccordionItemHeading>
                <AccordionItemButton color={primaryColor} className="flex items-center justify-between">
                  <div className="text-base">
                    <StyledHeading primaryColor={primaryColor}>{request.title}</StyledHeading>
                    <StyledSubTitle>{request.sub_title}</StyledSubTitle>
                  </div>
                  <StyledButton $borderColor={primaryBorderColor} $primaryColor={primaryColor}>
                    <AccordionItemState>
                      {({ expanded }) =>
                        expanded ? <AiOutlineMinusCircle size={30} /> : <AiOutlinePlusCircle size={30} />
                      }
                    </AccordionItemState>
                    {t('others.othDetails')}
                  </StyledButton>
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel className="mt-5">
                <div id="container-content" className="flex flex-row flex-wrap items-start justify-between">
                  <div className="w-2/6 min-w-[380px] pb-5 text-base text-[#727272]">
                    <div>
                      <p>{request.description}</p>
                    </div>
                  </div>

                  <div className="flex min-w-[360px] flex-row flex-wrap justify-center pb-5">
                    <div className="flex w-full flex-auto justify-center min-[320px]:justify-start md:justify-start">
                      <StyledProcessingTimeContainer>
                        <div className="flex flex-col px-2 py-2">
                          <StyledProcessingTime $primaryColor={primaryColor}>
                            {request.processing_time} {t('descriptions.ServicesDays')}
                          </StyledProcessingTime>
                          <span className="text-xs text-[#727272]">{t('OnboardingRequestProcessingTime')}</span>
                        </div>
                      </StyledProcessingTimeContainer>

                      <StyledValueContainer $primaryColor={primaryColor}>
                        <StyledValue $primaryColor={primaryColor}>{formatterValue.format(request?.value)}</StyledValue>
                      </StyledValueContainer>
                    </div>
                    <div
                      id="content-btn"
                      className="flex min-w-[320px] justify-center px-4 py-2 min-[320px]:justify-start min-[320px]:px-0 md:justify-start md:px-0"
                    >
                      <StyledModalButton onClick={handleShowModal(true, request.id)}>
                        {t('buttons.btnContinueToCheckout')}
                      </StyledModalButton>
                    </div>
                  </div>

                  <StyledStepsContainer>
                    <StyledSteps $primaryColor={primaryColor}>{t('OnboardingRequestStepsToExpect')}</StyledSteps>
                    <div id="steps-expect" dangerouslySetInnerHTML={{ __html: request.steps_expected }}></div>
                  </StyledStepsContainer>
                </div>
              </AccordionItemPanel>
            </StyledAccordionItem>
          </div>
        ))}
      </Accordion>

      <Dialog.Root open={showModal} onOpenChange={setModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-20 data-[state=open]:animate-overlayShow" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white pt-4 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none data-[state=open]:animate-contentShow">
            <Dialog.Title className="m-0 text-[17px] font-medium text-black">
              <h1 className="px-4 text-center">{t('validations.valAttetion')}</h1>
            </Dialog.Title>

            <Dialog.Description className="m-0 py-6 text-[14px] text-black">
              <h1 className="px-4 text-center">{t('checkoutIsSecureText')}</h1>
              <div className="flex items-center justify-center px-4 py-2">
                <StyledModalButtonHandle onClick={handleAddRequest}>
                  {t('OnboardingRequestActionSubmit')}
                </StyledModalButtonHandle>
              </div>
            </Dialog.Description>

            <Dialog.Close asChild>
              <button
                className="absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full text-violet11 hover:bg-violet4 focus:shadow-[0_0_0_2px] focus:shadow-violet7 focus:outline-none"
                aria-label="Close"
              >
                <RxCross2 />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};
